import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { DealOutcome, DealStage } from 'models/DealRoom';
import { ListingStage, ListingType } from 'models/Listing';
import { User } from 'models/User';
import { useTranslation } from 'react-i18next';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import ResolveImage from 'components/ResolveImage';
import { getImgUrl } from 'components/ResolveImage/utils';
import LinkToIcon from 'icons/LinkTo.icon';
import utils, { isSuperAdminOrOwner } from 'utils';
import TagsContainer from 'components/ListingPortfolioCard/components/TagsContainer';
import usePastTime from 'hooks/usePastTime';
import { Industry } from 'models/Industry';
import useMatchMedia from 'hooks/useMatchMedia';

import './WatchlistPortfolioCard.styles.scss';
import './WatchlistPortfolioCard.styles.responsive.scss';

type WatchlistPortfolioCardProps = {
  className?: string;
  dealStage: DealStage;
  listingStage: ListingStage;
  imageSrc: string;
  title: string;
  dealOutcome: DealOutcome;
  otherDealOutcome: string;
  isColleague: boolean;
  date: Date;
  listingType: ListingType;
  industry: Industry;
  expirationDate?: Date;
  seekingExpressionOfInterestDate?: string;
  ownerId: string;
  owner: Partial<User>;
  slugName: string;
};

const WatchlistPortfolioCard: React.FC<WatchlistPortfolioCardProps> = (
  props,
) => {
  const {
    className,
    imageSrc,
    title,
    dealStage,
    listingStage,
    dealOutcome,
    otherDealOutcome,
    isColleague,
    date,
    listingType,
    industry,
    expirationDate,
    seekingExpressionOfInterestDate,
    owner,
    ownerId,
    slugName,
  } = props;

  const { isSuperAdmin } = useContext(CurrentUserContext);
  const {
    currentUser: { _id: currentUserId },
  } = useContext(CurrentUserContext);

  const isOwnerOrSuperAdmin = isSuperAdminOrOwner(
    isSuperAdmin,
    ownerId,
    currentUserId,
  );

  const companyName = owner.companyName;

  const pastTime = usePastTime();
  const { t } = useTranslation();

  const isAbovePhablet = useMatchMedia('isAbovePhablet');

  const classes = classNames('watchlist-portfolio-card', className);

  const rerouteLink = useMemo(
    () => utils.composeBusinessUrl(industry, slugName, listingType),
    [industry, listingType, slugName],
  );

  const expiredDate = useMemo(() => {
    if (!seekingExpressionOfInterestDate && !expirationDate) return null;

    if (!expirationDate) return new Date(seekingExpressionOfInterestDate);

    if (!seekingExpressionOfInterestDate) return new Date(expirationDate);

    const seekingDate = new Date(seekingExpressionOfInterestDate);
    const exprDate = new Date(expirationDate);

    return seekingDate < exprDate ? seekingDate : exprDate;
  }, [expirationDate, seekingExpressionOfInterestDate]);

  return (
    <div className={classes}>
      <div className="watchlist-portfolio-card__left">
        <div className="watchlist-portfolio-card__left__heading">
          <Link
            to={rerouteLink}
            className="watchlist-portfolio-card__left__heading__title"
          >
            {title}
          </Link>
          <Link
            to={rerouteLink}
            className="watchlist-portfolio-card__left__heading__deal-info"
          >
            <span>
              {dealOutcome === 'other' ? otherDealOutcome : t(dealOutcome)}
            </span>
            <span>{industry}</span>
          </Link>
        </div>

        {isAbovePhablet && (
          <TagsContainer
            isColleague={isColleague}
            isOwnerOrSuperAdmin={isOwnerOrSuperAdmin}
            dealStage={dealStage}
            listingStage={listingStage}
            listingType={listingType}
            date={date}
            expiredDate={expiredDate}
          />
        )}

        <div className="watchlist-portfolio-card__left__actions">
          <Link to={rerouteLink} className="border">
            <LinkToIcon />
            {t('seeBuyersView')}
          </Link>
        </div>

        {isSuperAdmin && (
          <p className="watchlist-portfolio-card__left__represented">
            {owner.lastSeen && <span>{pastTime(owner.lastSeen)}</span>}
            <span>{`${owner.firstName} ${owner.lastName}`}</span>
            {companyName && <span>{companyName}</span>}
          </p>
        )}
      </div>

      <div className="watchlist-portfolio-card__image-container">
        <Link aria-label="Business details page" to={rerouteLink}>
          <ResolveImage src={getImgUrl(imageSrc, '480')} alt="Listing" />
        </Link>
      </div>
    </div>
  );
};

export default WatchlistPortfolioCard;
